import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { apiBaseUrl } from '../../util/api';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import css from './TrainerPricingPage.module.css';

// Feature rows shown in the comparison table
const FEATURES = [
  { label: 'Client messaging',         tiers: ['free', 'group_fitness', 'starter', 'pro', 'elite'] },
  { label: 'Session scheduling',       tiers: ['free', 'group_fitness', 'starter', 'pro', 'elite'] },
  { label: 'Progress tracking',        tiers: ['free', 'group_fitness', 'starter', 'pro', 'elite'] },
  { label: 'Profile branding',         tiers: ['free', 'group_fitness', 'starter', 'pro', 'elite'] },
  { label: 'Group class management',   tiers: ['group_fitness', 'starter', 'pro', 'elite'] },
  { label: 'Workout templates',        tiers: ['free', 'starter', 'pro', 'elite'] },
  { label: 'Training plans',           tiers: ['free', 'starter', 'pro', 'elite'] },
  { label: 'Nutrition plans',          tiers: ['free', 'starter', 'pro', 'elite'] },
  { label: 'AI fitness assistant',     tiers: ['free', 'group_fitness', 'starter', 'pro', 'elite'] },
  { label: 'Business cards',           tiers: ['pro', 'elite'] },
  { label: 'Priority support',         tiers: ['pro', 'elite'] },
  { label: 'White glove onboarding',   tiers: ['pro', 'elite'] },
];

// Tier highlights shown on each card (marketing copy)
const TIER_HIGHLIGHTS = {
  free:          ['Up to 2 clients', 'Session scheduling', 'Client messaging', 'AI fitness assistant'],
  group_fitness: ['Unlimited clients', 'Group class management', 'Session scheduling', 'AI fitness assistant'],
  starter:       ['Up to 5 clients', 'Full workout & nutrition suite', 'Training plan builder', 'AI fitness assistant'],
  pro:           ['Unlimited clients', 'Everything in Starter', 'White glove onboarding', 'Priority support', 'Business cards'],
  elite:         ['Unlimited clients', 'Everything in Pro', 'White glove onboarding', 'Business cards'],
};

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="8" fill="#2ebff7" fillOpacity="0.15" />
    <path d="M4.5 8l2.5 2.5 4.5-5" stroke="#2ebff7" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M5 5l6 6M11 5l-6 6" stroke="#d1d5db" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
);

const BulletCheck = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" className={css.bulletIcon}>
    <circle cx="7" cy="7" r="7" fill="#2ebff7" fillOpacity="0.15" />
    <path d="M3.5 7l2.5 2.5 4-4.5" stroke="#2ebff7" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TrainerPricingPageComponent = ({ scrollingDisabled, currentUser }) => {
  const history = useHistory();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [showTable, setShowTable] = useState(false);

  useEffect(() => {
    fetch(`${apiBaseUrl()}/api/subscriptions/tiers`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.success) setTiers(data.tiers); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const getPrice = tier => {
    if (tier.tierName === 'free') return { amount: 'Free', period: null };
    const amount = billingCycle === 'annual'
      ? `$${tier.annualPrice}`
      : `$${tier.monthlyPrice}`;
    const period = billingCycle === 'annual' ? '/yr' : '/mo';
    return { amount, period };
  };

  const getSavingsPct = tier => {
    if (!tier.annualPrice || tier.tierName === 'free') return null;
    const annual = tier.monthlyPrice * 12;
    return Math.round(((annual - tier.annualPrice) / annual) * 100);
  };

  const handleCTA = tier => {
    if (currentUser) {
      history.push('/subscription-pricing');
    } else {
      history.push(`/signup?from=pricing&tier=${tier.tierName}`);
    }
  };

  const isPopular = tier => tier.tierName === 'pro';
  const highlights = tier => TIER_HIGHLIGHTS[tier.tierName] || [];

  // Display only the 3 paid tiers from the brief: group_fitness, starter, pro/elite
  // Show free as a soft option below
  const paidTierNames = ['group_fitness', 'starter', 'pro', 'elite'];
  const displayTiers = tiers.filter(t => paidTierNames.includes(t.tierName));
  const freeTier = tiers.find(t => t.tierName === 'free');

  return (
    <Page
      title="Trainer Pricing — GymNet"
      description="Choose a GymNet plan and start growing your training business. Plans from $19/mo with group fitness management, workout builders, nutrition plans, and AI tools."
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn>
        <TopbarContainer />

        <div className={css.page}>

          {/* ── Hero ─────────────────────────────────────────────── */}
          <section className={css.hero}>
            <div className={css.heroInner}>
              <div className={css.heroPill}>For Personal Trainers</div>
              <h1 className={css.heroTitle}>Simple, transparent pricing</h1>
              <p className={css.heroSub}>
                Everything you need to run your fitness business — from group classes to 1-on-1 coaching, custom training plans, and AI-powered tools.
              </p>

              {/* Billing toggle */}
              <div className={css.billingToggle}>
                <button
                  className={billingCycle === 'monthly' ? css.toggleActive : css.toggleBtn}
                  onClick={() => setBillingCycle('monthly')}
                >
                  Monthly
                </button>
                <button
                  className={billingCycle === 'annual' ? css.toggleActive : css.toggleBtn}
                  onClick={() => setBillingCycle('annual')}
                >
                  Annual
                  <span className={css.saveBadge}>Save up to 17%</span>
                </button>
              </div>
            </div>
          </section>

          {/* ── Tier cards ───────────────────────────────────────── */}
          <section className={css.cardsSection}>
            <div className={css.cardsInner}>
              {loading ? (
                <div className={css.loadingRow}>
                  <div className={css.spinner} />
                  <span>Loading plans…</span>
                </div>
              ) : (
                <div className={css.cards}>
                  {displayTiers.map(tier => {
                    const { amount, period } = getPrice(tier);
                    const savingsPct = getSavingsPct(tier);
                    const popular = isPopular(tier);

                    return (
                      <div key={tier.id} className={`${css.card} ${popular ? css.cardPopular : ''}`}>
                        {popular && <div className={css.popularBadge}>Most Popular</div>}

                        <div className={css.cardHeader}>
                          <div className={css.tierName}>{tier.displayName}</div>
                          <div className={css.clientLimit}>
                            {tier.clientLimit === 999999
                              ? 'Unlimited clients'
                              : `Up to ${tier.clientLimit} client${tier.clientLimit !== 1 ? 's' : ''}`}
                          </div>
                        </div>

                        <div className={css.priceRow}>
                          <span className={css.price}>{amount}</span>
                          {period && <span className={css.pricePeriod}>{period}</span>}
                        </div>
                        {billingCycle === 'annual' && savingsPct && (
                          <div className={css.savingsNote}>Save {savingsPct}% vs monthly</div>
                        )}

                        <button
                          className={popular ? css.ctaPrimary : css.ctaSecondary}
                          onClick={() => handleCTA(tier)}
                        >
                          {currentUser ? 'Manage Plan' : 'Get Started'}
                        </button>

                        <ul className={css.featureList}>
                          {highlights(tier).map(item => (
                            <li key={item} className={css.featureItem}>
                              <BulletCheck />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Free tier soft mention */}
              {!loading && freeTier && (
                <p className={css.freeNote}>
                  Just getting started?{' '}
                  <button className={css.freeTierLink} onClick={() => handleCTA(freeTier)}>
                    Try the free plan
                  </button>{' '}
                  — up to {freeTier.clientLimit} clients, no credit card required.
                </p>
              )}
            </div>
          </section>

          {/* ── AI callout ───────────────────────────────────────── */}
          <section className={css.aiSection}>
            <div className={css.aiInner}>
              <div className={css.aiIcon}>✦</div>
              <div>
                <h2 className={css.aiTitle}>AI fitness assistant included on every plan</h2>
                <p className={css.aiDesc}>
                  Generate personalised workout and nutrition plans in seconds, get coaching suggestions, and answer client questions — all powered by AI built for fitness professionals.
                </p>
              </div>
            </div>
          </section>

          {/* ── Full comparison table ─────────────────────────────── */}
          <section className={css.tableSection}>
            <div className={css.tableInner}>
              <button
                className={css.tableToggle}
                onClick={() => setShowTable(v => !v)}
                aria-expanded={showTable}
              >
                {showTable ? 'Hide' : 'Show'} full feature comparison
                <span className={`${css.chevron} ${showTable ? css.chevronUp : ''}`}>▾</span>
              </button>

              {showTable && !loading && displayTiers.length > 0 && (
                <div className={css.tableWrap}>
                  <table className={css.table}>
                    <thead>
                      <tr>
                        <th className={css.featureCol} />
                        {displayTiers.map(tier => (
                          <th key={tier.id} className={`${css.tierCol} ${isPopular(tier) ? css.popularCol : ''}`}>
                            <div className={css.tableHeadName}>{tier.displayName}</div>
                            <div className={css.tableHeadPrice}>{getPrice(tier).amount}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {FEATURES.map(feature => (
                        <tr key={feature.label} className={css.featureRow}>
                          <td className={css.featureLabel}>{feature.label}</td>
                          {displayTiers.map(tier => {
                            const included = feature.tiers.includes(tier.tierName);
                            return (
                              <td key={tier.id} className={css.featureCell}>
                                {included ? <CheckIcon /> : <XIcon />}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>

          {/* ── FAQ ──────────────────────────────────────────────── */}
          <section className={css.faqSection}>
            <div className={css.faqInner}>
              <h2 className={css.faqTitle}>Common questions</h2>
              <div className={css.faqs}>
                {[
                  {
                    q: 'Can I change my plan later?',
                    a: 'Yes. You can upgrade or downgrade at any time from your account settings. Changes take effect immediately and are prorated on your next invoice.',
                  },
                  {
                    q: 'Is there a free trial?',
                    a: 'GymNet offers a permanent free tier (up to 2 clients) so you can explore the platform before committing to a paid plan.',
                  },
                  {
                    q: 'What counts as a client?',
                    a: 'A client is any user you have an active coaching relationship with. Clients can message you, book sessions, and access plans you assign them.',
                  },
                  {
                    q: 'How does annual billing work?',
                    a: 'Annual plans are billed once a year at a discounted rate — up to 17% cheaper than paying monthly. You can cancel before your renewal date for a prorated refund.',
                  },
                  {
                    q: 'What is the AI fitness assistant?',
                    a: 'All GymNet trainers get access to an AI assistant trained on fitness and nutrition science. Use it to generate workout templates, build nutrition plans, and answer client questions — right inside the platform.',
                  },
                ].map(({ q, a }) => (
                  <div key={q} className={css.faqItem}>
                    <h3 className={css.faqQ}>{q}</h3>
                    <p className={css.faqA}>{a}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── Final CTA ────────────────────────────────────────── */}
          <section className={css.ctaSection}>
            <div className={css.ctaInner}>
              <h2 className={css.ctaTitle}>Ready to grow your training business?</h2>
              <p className={css.ctaSub}>Join GymNet — background-checked, professional, and built for trainers.</p>
              <div className={css.ctaButtons}>
                <button
                  className={css.ctaMainBtn}
                  onClick={() => history.push(currentUser ? '/subscription-pricing' : '/signup?from=pricing')}
                >
                  Get started free
                </button>
                <button
                  className={css.ctaGhostBtn}
                  onClick={() => history.push('/s')}
                >
                  Browse trainers
                </button>
              </div>
              <p className={css.ctaMeta}>No credit card required · Cancel anytime</p>
            </div>
          </section>

        </div>

        <FooterContainer />
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
  currentUser: state.user?.currentUser,
});

export default compose(connect(mapStateToProps))(TrainerPricingPageComponent);
